import React, { useContext, useEffect, useState } from "react";
import DeviceTypeField from "./DeviceTypeField";
import SampleLogField from "./SampleLogField";
import * as deviceService from "../../devices/deviceService";
import EventKeywordField from "./EventKeywordField";
import EventList from "./EventList";
import EventListManagement from "./EventListManagement";
import { PopupContext } from "../../index";
import { getConfirmPopup, POPUP_TYPES } from "../../view/popup/PopupContainer";
import { EventType } from "../../vars";
import { changeEvent, deleteEvent, saveEvent } from "../ruleService";
import { showSuccessToast } from "../../validation/ToastContainer";
// import * as ruleService from "../ruleService";
// import * as storeService from "../add-rules/addRuleStoreService";
import { deepCopy } from "../../utils";
import { Shield, ChevronDown } from "lucide-react";
import FetchDeviceConfigCommandSection from "../../system/system-settings/FetchDeviceConfigCommandSection";
import AdditionalSSHAlgorithmOptionsSection from "../../system/additionalSSHAlgorithm/AdditionalSSHAlgorithmOptionsSection";

function SettingsCard({ children }) {
    return (
        <div className="card oct_analysis-card-v2 w-100 p-2">
            <div className="card-body">
                <div className="dataTables_wrapper dt-bootstrap4 no-footer">
                    {children}
                </div>
            </div>
        </div>
    );
}

function SyslogSettingsSection() {

    const initialState = {
        //sampleLog: null,
        sampleLog: "",
        eventKeyword: null,
        selectedEvent: null,
        selectedDeviceType: null
    }

    const { setPopup } = useContext(PopupContext);
    const [state, setState] = useState(initialState);
    const [deviceTypes, setDeviceTypes] = useState([]);
    const [events, setEvents] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(Date.now);

    const [keywordsCollapsed, setKeywordsCollapsed] = useState(true);
    const [mainCollapsed, setMainCollapsed] = useState(true);

    const fetchData = async () => {
        try {
            const eventList = await deviceService.eventsList();
            const deviceTypeList = await deviceService.loadDeviceTypes();
            setState({ ...state, selectedDeviceType: deviceTypeList[0] });
            setDeviceTypes(deviceTypeList);
            setEvents(eventList);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    useEffect(() => {
        fetchData();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [lastUpdated]);

    const onSampleLogChange = (value) => setState({ ...state, sampleLog: value });
    const onEventKeywordChange = (value) => setState({ ...state, eventKeyword: value });
    const onDeviceTypeChange = (value) => setState({ ...state, selectedDeviceType: value, selectedEvent: null });

    const confirmEventCreation = async (event) => {
        const newEvent = {
            name: event,
            deviceType: state.selectedDeviceType,
            value: event,
            type: EventType.KEYWORD
        }
        await saveEvent(newEvent);
        setState({ ...state, eventKeyword: null });
        setLastUpdated(Date.now);
    }

    const confirmEventEditing = async (event) => {
        await changeEvent(event);
        setState({ ...state, selectedEvent: null });
        setLastUpdated(Date.now);
    }

    // const onAddEventClick = () => setPopup(
    //     {
    //         type: POPUP_TYPES.CREATE_EVENT,
    //         eventKeyword: state.eventKeyword,
    //         confirm: confirmEventCreation
    //     }
    // );

    const onEditEventClick = () => state.selectedEvent && setPopup(
        {
            type: POPUP_TYPES.EDIT_EVENT,
            selectedEvent: deepCopy(state.selectedEvent),
            confirm: confirmEventEditing
        }
    )

    const onRemoveEventClick = async () => {
        setPopup(
            getConfirmPopup(
                <> <p>Are you sure?</p> <p>Devices with selected event will be updated.</p> </>,
                'Remove event',
                async () => {
                    const result = await deleteEvent(state.selectedEvent.id);
                    result && showSuccessToast("Event removed", "Remove selected event");
                    setLastUpdated(Date.now());
                }
            )
        )
    };

    const setSelectedEvent = (value) => {
        const newEvent = state.selectedEvent ? (state.selectedEvent.id === value.id ? null : value) : value;
        setState({ ...state, selectedEvent: newEvent })
    };

    const getEvents = () => {
        if (state.selectedDeviceType) {
            return events.filter(event => event?.deviceType?.id === state.selectedDeviceType.id)
        } else {
            return events;
        }
    }

    return (
        <div className="oct_analysis-card-v2 p-4 m-4">
            <button
                className="oct_settings-section-header"
                onClick={() => setMainCollapsed(!mainCollapsed)}
            >
                <h3 className="mb-0 text-base font-bold text-slate-800">Device Syslog Events Settings</h3>
                <ChevronDown
                    className={`oct_icon oct_settings-chevron-v2 ${!mainCollapsed ? 'oct_expanded' : ''}`}
                />
            </button>

            <div className={`oct_settings-section-body ${mainCollapsed ? 'oct_collapsed' : 'oct_expanded'}`}>

                <div className="row mt-4">
                    <div className="col-12 space-y-6">
                        <SettingsCard>
                            <button
                                className="oct_settings-section-header"
                                onClick={() => setKeywordsCollapsed(!keywordsCollapsed)}
                            >
                                <h3>Sample Log & Event Keywords Configuration</h3>
                                <ChevronDown className={`oct_icon oct_settings-chevron-v2 ${!keywordsCollapsed ? 'oct_expanded' : ''}`} />
                            </button>

                            <div className={`oct_settings-section-body ${keywordsCollapsed ? 'oct_collapsed' : 'oct_expanded'}`}>
                                <div className="oct_info-banner-v2 mt-4 mb-4">
                                    <p>
                                        Define the syslog events that will be identified by Octopus VAR in order to trigger realtime configuration change analysis when that event occurs.
                                    </p>
                                </div>

                                <div className="row">
                                    <div className="col-lg-6">
                                        <div className="mt-4 mb-4 pb-4 border-bottom">
                                            <SampleLogField value={state.sampleLog} onChange={onSampleLogChange} />
                                        </div>

                                        <div className="mb-4">
                                            <DeviceTypeField deviceTypes={deviceTypes} value={state.selectedDeviceType} onChange={onDeviceTypeChange} />
                                        </div>

                                        <EventKeywordField value={state.eventKeyword || ''} onChange={onEventKeywordChange} />

                                        <div className="d-flex align-items-center oct_syslog-flex-gap mt-4">
                                            <button
                                                type="button"
                                                disabled={state.eventKeyword === null || state.eventKeyword === ""}
                                                onClick={() => confirmEventCreation(state.eventKeyword)}
                                                className="flex-1 oct_btn-export-v2 w-100 d-flex justify-content-center align-items-center oct_syslog-btn-base"
                                            >
                                                <Shield className="w-4 h-4 mr-2" />
                                                Add Keyword
                                            </button>

                                            {/* <button
                                                type="button"
                                                disabled={true}
                                                className="flex-1 oct_btn-export-v2 w-100 d-flex justify-content-center align-items-center oct_syslog-btn-primary"
                                            >
                                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                                Validate RegEx
                                            </button> */}
                                        </div>
                                    </div>

                                    <div className="col-lg-6 mt-4">
                                        <div className="oct_syslog-text-v2">
                                            <h3 className="text-base font-bold text-slate-800 mb-1">Configured Event Keywords</h3>
                                            <p className="text-xs text-slate-500 mb-0">
                                                The following keywords are configured for <span className="font-weight-bold text-slate-700">{state.selectedDeviceType?.name || 'the selected device type'}</span>
                                            </p>
                                        </div>

                                        <div className="mt-4">
                                            <EventList events={getEvents()} selectedEvent={state.selectedEvent} setSelectedEvent={setSelectedEvent} />
                                        </div>

                                        <EventListManagement disabled={!state.selectedEvent} onEditClick={onEditEventClick} onRemoveLick={onRemoveEventClick} selectedEvent={state.selectedEvent} />
                                    </div>
                                </div>
                            </div>
                        </SettingsCard>
                    </div>
                </div>

                <div className="oct_settings-divider my-4"></div>
                {/* Additional Settings */}
                <div className="row mt-4">
                    <div className="col-lg-6 space-y-6">
                        <SettingsCard>
                            <FetchDeviceConfigCommandSection />
                        </SettingsCard>
                    </div>
                    <div className="col-lg-6 space-y-6 mt-4 mt-lg-0">
                        <SettingsCard>
                            <AdditionalSSHAlgorithmOptionsSection />
                        </SettingsCard>
                    </div>
                </div>

            </div>
        </div>
    )

}

export default SyslogSettingsSection;
